// Lexer for dwsql — datawiki's flavour of parameterized SQL.
//
// It does NOT parse SQL structure. It only classifies the source into spans so
// that our two sigils are interpreted in *code* regions and left alone inside
// strings, comments, and dollar-quoted bodies:
//
//   $name        a bound parameter        (kind: "param")
//   @ref         an include reference     (kind: "include")
//
// Everything else passes through verbatim as "code" (or as a recognized
// string/comment/dollar-string token). Because the lexer skips literals and
// comments, `'$x'` and `-- $x` and `$$ ... $x ... $$` never look like params.

export type TokenKind =
  | "code"
  | "string" // '...'  (with '' escapes)
  | "ident" // "..."  (quoted identifier, with "" escapes)
  | "lineComment" // -- ... to end of line
  | "blockComment" // /* ... */  (nestable, per Postgres)
  | "dollarString" // $tag$ ... $tag$  (tag optional)
  | "param" // $name
  | "include"; // @ref

export interface Token {
  kind: TokenKind;
  text: string;
  /** For kind === "param": the name without the leading `$`. */
  name?: string;
  /** For kind === "include": the reference without the leading `@`. */
  ref?: string;
}

const isParamStart = (c: string): boolean => /[A-Za-z_]/.test(c);
const isParamChar = (c: string): boolean => /[A-Za-z0-9_]/.test(c);
// Include refs look like paths: they must NOT start with a digit (so `@5` stays
// the Postgres abs operator) and not with `-`/`>`/`@` (so `@>`, `@@`, `@-@`,
// and `@ x` stay operators).
const isRefStart = (c: string): boolean => /[A-Za-z_./]/.test(c);
const isRefChar = (c: string): boolean => /[A-Za-z0-9_./-]/.test(c);

const DOLLAR_OPEN = /^\$([A-Za-z_]\w*)?\$/;

export function tokenize(sql: string): Token[] {
  const tokens: Token[] = [];
  let code = "";
  const flush = (): void => {
    if (code) {
      tokens.push({ kind: "code", text: code });
      code = "";
    }
  };

  let i = 0;
  const n = sql.length;
  while (i < n) {
    const c = sql[i];
    const c2 = sql.slice(i, i + 2);

    // -- line comment
    if (c2 === "--") {
      flush();
      let j = i + 2;
      while (j < n && sql[j] !== "\n") j++;
      tokens.push({ kind: "lineComment", text: sql.slice(i, j) });
      i = j;
      continue;
    }

    // /* block comment */ (nestable)
    if (c2 === "/*") {
      flush();
      let j = i + 2;
      let depth = 1;
      while (j < n && depth > 0) {
        if (sql.slice(j, j + 2) === "/*") {
          depth++;
          j += 2;
        } else if (sql.slice(j, j + 2) === "*/") {
          depth--;
          j += 2;
        } else {
          j++;
        }
      }
      tokens.push({ kind: "blockComment", text: sql.slice(i, j) });
      i = j;
      continue;
    }

    // '...' string literal
    if (c === "'") {
      flush();
      let j = i + 1;
      while (j < n) {
        if (sql[j] === "'") {
          if (sql[j + 1] === "'") {
            j += 2; // escaped ''
            continue;
          }
          j++;
          break;
        }
        j++;
      }
      tokens.push({ kind: "string", text: sql.slice(i, j) });
      i = j;
      continue;
    }

    // "..." quoted identifier
    if (c === '"') {
      flush();
      let j = i + 1;
      while (j < n) {
        if (sql[j] === '"') {
          if (sql[j + 1] === '"') {
            j += 2; // escaped ""
            continue;
          }
          j++;
          break;
        }
        j++;
      }
      tokens.push({ kind: "ident", text: sql.slice(i, j) });
      i = j;
      continue;
    }

    // $ — dollar-quoted string, or a $param
    if (c === "$") {
      const dq = DOLLAR_OPEN.exec(sql.slice(i));
      if (dq) {
        // $tag$ ... $tag$ takes precedence over a $name param, matching
        // Postgres. (So authors avoid writing `$name$`.)
        flush();
        const tag = dq[0];
        const close = sql.indexOf(tag, i + tag.length);
        const end = close === -1 ? n : close + tag.length;
        tokens.push({ kind: "dollarString", text: sql.slice(i, end) });
        i = end;
        continue;
      }
      if (i + 1 < n && isParamStart(sql[i + 1])) {
        flush();
        let j = i + 1;
        while (j < n && isParamChar(sql[j])) j++;
        const name = sql.slice(i + 1, j);
        tokens.push({ kind: "param", text: sql.slice(i, j), name });
        i = j;
        continue;
      }
      // Bare `$` (e.g. a stray `$1` positional placeholder) — leave as code.
      code += c;
      i++;
      continue;
    }

    // @ref — include
    if (c === "@" && i + 1 < n && isRefStart(sql[i + 1])) {
      flush();
      let j = i + 1;
      while (j < n && isRefChar(sql[j])) j++;
      const ref = sql.slice(i + 1, j);
      tokens.push({ kind: "include", text: sql.slice(i, j), ref });
      i = j;
      continue;
    }

    code += c;
    i++;
  }

  flush();
  return tokens;
}
